import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Home as HomeIcon, IndianRupee, Building2, Factory, Trees, ShieldCheck, HeadphonesIcon, Award } from "lucide-react";
import { HERO_IMG, LOCALITIES } from "@/data/constants";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const tags = ["Flats","Apartments","Villas","Shops","Office","Showroom","Warehouse","Industrial Shed","Plots"];

export default function HeroSearch() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [purpose, setPurpose] = useState("sale");
  const [category, setCategory] = useState("");
  const [locality, setLocality] = useState("");
  const [budget, setBudget] = useState("");

  const applyBudget = (params, b) => {
    if (b === "25") params.set("max_price", "2500000");
    else if (b === "50") params.set("max_price", "5000000");
    else if (b === "75") params.set("max_price", "7500000");
    else if (b === "100") params.set("max_price", "10000000");
    else if (b === "above") params.set("min_price", "10000000");
  };

  const search = () => {
    const p = new URLSearchParams({ purpose });
    if (category) p.set("category", category);
    if (locality) p.set("locality", locality);
    if (budget) applyBudget(p, budget);
    nav(`/properties?${p.toString()}`);
  };

  const tagSearch = (tag) => {
    const p = new URLSearchParams({ purpose });
    p.set("q", tag);
    nav(`/properties?${p.toString()}`);
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Surat skyline" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 hero-overlay"/>
      </div>
      <div className="relative max-w-[1400px] mx-auto px-4 py-16 lg:py-24 grid lg:grid-cols-[1.4fr,1fr] gap-10 items-center">
        <div className="animate-fade-up">
          <h1 className="font-display text-white text-4xl sm:text-5xl font-bold leading-[1.1] max-w-3xl">
            {t("hero.title1")}
            <span className="inline-block text-white bg-kp-maroon px-3 py-0.5 rounded-lg ml-2 align-baseline">{t("hero.city")}</span>
          </h1>
          <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl">{t("hero.subtitle")}</p>

          <div className="mt-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/40 p-4 sm:p-5" data-testid="hero-search-panel">
            <Tabs value={purpose} onValueChange={setPurpose}>
              <TabsList className="bg-slate-100 rounded-full p-1 h-auto">
                <TabsTrigger value="sale" className="rounded-full data-[state=active]:bg-[#800020] data-[state=active]:text-white text-slate-700 px-6 py-2 text-sm font-semibold" data-testid="tab-buy">{t("hero.buy")}</TabsTrigger>
                <TabsTrigger value="rent,lease" className="rounded-full data-[state=active]:bg-[#800020] data-[state=active]:text-white text-slate-700 px-6 py-2 text-sm font-semibold" data-testid="tab-rent">{t("hero.rent")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="search-category"><SelectValue placeholder={t("hero.cat")}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                  <SelectItem value="plot">Plot / Land</SelectItem>
                </SelectContent>
              </Select>
              <Select value={locality} onValueChange={setLocality}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="search-locality"><SelectValue placeholder={`${t("hero.loc")} (Surat)`}/></SelectTrigger>
                <SelectContent className="max-h-72">{LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={budget} onValueChange={setBudget}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="search-budget"><SelectValue placeholder={t("hero.budget")}/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">Up to ₹25 L</SelectItem>
                  <SelectItem value="50">Up to ₹50 L</SelectItem>
                  <SelectItem value="75">Up to ₹75 L</SelectItem>
                  <SelectItem value="100">Up to ₹1 Cr</SelectItem>
                  <SelectItem value="above">Above ₹1 Cr</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={search} className="mt-4 btn-primary rounded-full h-12 w-full sm:w-auto sm:px-10 gap-2 font-semibold" data-testid="hero-search-button">
              <Search size={18}/> {t("hero.search")}
            </Button>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider mr-2">Popular:</span>
              {tags.map((tag) => (
                <button key={tag} onClick={() => tagSearch(tag)} className="text-xs px-3 py-1.5 rounded-full bg-kp-teal-light text-kp-teal hover:bg-kp-teal hover:text-white transition-colors" data-testid={`popular-tag-${tag.toLowerCase().replace(/\s+/g,'-')}`}>
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hidden lg:flex flex-col gap-4">
          {[
            { icon: ShieldCheck, title: t("hero.verified"), sub: t("hero.verified_sub") },
            { icon: HeadphonesIcon, title: t("hero.expert"), sub: t("hero.expert_sub") },
            { icon: Award, title: t("hero.best"), sub: t("hero.best_sub") },
          ].map((it, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 flex items-center gap-4 hover:bg-white/15 transition-colors">
              <div className="w-14 h-14 rounded-full bg-kp-maroon flex items-center justify-center flex-shrink-0"><it.icon className="text-white" size={26}/></div>
              <div>
                <div className="font-display font-semibold text-white text-lg">{it.title}</div>
                <div className="text-sm text-white/70">{it.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
